import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { todayNZ } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CalendarPlus, Send } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  declined: "bg-rose-100 text-rose-800 border-rose-300",
};

const fmt = (s) => (s ? s.split("-").reverse().join("/") : "");

export default function LeaveRequestDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const today = todayNZ();
  const [type, setType] = useState("");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState("");
  const [medCert, setMedCert] = useState(false);
  const [declared, setDeclared] = useState(false);

  const { data: labels } = useQuery({
    queryKey: ["ts-labels"],
    queryFn: async () => (await api.get("/timesheets/labels")).data,
    enabled: open,
  });
  const { data: requests = [] } = useQuery({
    queryKey: ["my-leave-requests"],
    queryFn: async () => (await api.get("/my/leave-requests")).data,
    enabled: open,
  });

  const isSick = /sick|bereavement/i.test(type);
  const singleDay = start === end;
  const totalDays = (() => {
    if (!start || !end || end < start) return 0;
    const days = Math.round((new Date(end + "T00:00:00") - new Date(start + "T00:00:00")) / 86400000) + 1;
    return singleDay && halfDay ? 0.5 : days;
  })();

  function reset() {
    setType(""); setStart(today); setEnd(today); setHalfDay(false);
    setReason(""); setMedCert(false); setDeclared(false);
  }

  const submit = useMutation({
    mutationFn: () =>
      api.post("/my/leave-requests", {
        leave_type: type,
        start_date: start,
        end_date: end,
        half_day: singleDay && halfDay,
        reason,
        med_cert: isSick && medCert,
        declaration: declared,
      }),
    onSuccess: () => {
      toast.success(t("Leave request sent to your manager"));
      reset();
      qc.invalidateQueries({ queryKey: ["my-leave-requests"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || t("Could not submit request")),
  });

  const canSubmit = type && start && end && end >= start && declared && !submit.isPending;

  const options = labels
    ? [...(labels.recent || []), ...(labels.all || []).filter((l) => !(labels.recent || []).includes(l))]
    : [];

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="leave-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5" /> {t("Request leave")}
          </DialogTitle>
          <DialogDescription>{t("Pick your dates and the type of leave. Your manager is notified.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("Type of leave")}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder={t("Select leave type")} /></SelectTrigger>
              <SelectContent>
                {options.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("From")}</Label>
              <DatePicker value={start} onChange={(v) => { setStart(v); if (end < v) setEnd(v); }} testid="leave-start-picker" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("To")}</Label>
              <DatePicker value={end} onChange={setEnd} testid="leave-end-picker" />
            </div>
          </div>

          {singleDay && (
            <label className="flex items-center gap-2 text-sm" data-testid="leave-halfday-row">
              <Checkbox checked={halfDay} onCheckedChange={(v) => setHalfDay(!!v)} data-testid="leave-halfday-check" />
              {t("This is a half day")}
            </label>
          )}

          <div
            className="flex items-center justify-between rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm"
            data-testid="leave-total-days"
          >
            <span className="text-muted-foreground">{t("Total requested")}</span>
            <span className="font-display text-base font-extrabold text-indigo-700">
              {t("{{count}} day(s)", { count: totalDays })}
            </span>
          </div>

          <div className="space-y-1.5">
            <Label>{isSick ? t("Reason (recommended)") : t("Reason (optional)")}</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isSick ? t("e.g. unwell / medical appointment") : t("Add a short note for your manager")}
              rows={2}
              data-testid="leave-reason-input"
            />
          </div>

          {isSick && (
            <label className="flex items-center gap-2 text-sm" data-testid="leave-medcert-row">
              <Checkbox checked={medCert} onCheckedChange={(v) => setMedCert(!!v)} data-testid="leave-medcert-check" />
              {t("Medical certificate provided / to follow")}
            </label>
          )}

          <label className="flex items-start gap-2 rounded-md border border-border bg-secondary/40 p-3 text-sm" data-testid="leave-declaration-row">
            <Checkbox checked={declared} onCheckedChange={(v) => setDeclared(!!v)} data-testid="leave-declaration-check" className="mt-0.5" />
            <span>{t("I confirm this leave request is true and correct and made in good faith.")}</span>
          </label>

          <Button onClick={() => submit.mutate()} disabled={!canSubmit} className="w-full gap-2" data-testid="submit-leave-btn">
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Submit request")}
          </Button>
        </div>

        {requests.length > 0 && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="label-caps mb-2 text-muted-foreground">{t("My requests")}</div>
            <div className="space-y-2" data-testid="my-leave-list">
              {requests.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-md border border-border p-2.5 text-sm" data-testid={`leave-item-${r.id}`}>
                  <div>
                    <div className="font-semibold">{r.leave_type}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmt(r.start_date)}{r.start_date !== r.end_date ? ` – ${fmt(r.end_date)}` : ""} · {r.total_days} day{r.total_days === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase ${STATUS_STYLES[r.status] || ""}`} data-testid={`leave-status-${r.id}`}>
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
